import React, { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import axios from 'axios';
import { useToast } from './ui/use-toast';

const API_BASE = process.env.REACT_APP_BACKEND_URL + '/api';

const FundingTermsModal = ({ deal, onClose, onSuccess }) => {
  const { toast } = useToast();
  const initialTermStr = deal.term || '';
  const initialTermParts = initialTermStr.split('/');
  const initialFactor = (initialTermParts.length === 2 && !isNaN(parseFloat(initialTermParts[1])))
    ? parseFloat(initialTermParts[1]).toString()
    : '';
  const [formData, setFormData] = useState({
    funding_amount: deal.advanced || '',
    payback_amount: deal.payback || '',
    payment_frequency: 'daily',
    payment_amount: deal.daily || deal.weekly || '',
    first_payment_date: '',
    term_length: deal.term?.split('/')[0] || '',
    term_input: deal.term || '',
    factor_rate: initialFactor,
    funding_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [loading, setLoading] = useState(false);

  // Helper: recompute payback + payment amount from funded × factor / term_length
  const recomputeDerived = (state) => {
    const funding = parseFloat(state.funding_amount);
    const factor = parseFloat(state.factor_rate);
    const term = parseInt(state.term_length);
    if (!isNaN(funding) && !isNaN(factor) && funding > 0 && factor > 0) {
      const payback = funding * factor;
      state.payback_amount = payback.toFixed(2);
      if (!isNaN(term) && term > 0) {
        state.payment_amount = (payback / term).toFixed(2);
      }
    }
    // Keep the combined term_input string in sync so the existing backend pipeline still works
    if (state.term_length && state.factor_rate) {
      state.term_input = `${state.term_length}/${state.factor_rate}`;
    }
    return state;
  };

  // Auto-calculation handler — supports BOTH the legacy combined "Term/Factor" input
  // AND the new separate Factor Rate / Term Length / Funding Amount inputs.
  const handleTermOrAmountChange = (field, value) => {
    const newFormData = { ...formData, [field]: value };
    
    if (field === 'term_input') {
      // Parse term/factor format (e.g., "110/1.45") and sync the separate fields
      const parts = value.split('/');
      if (parts.length === 2) {
        const termLength = parseInt(parts[0]);
        const factor = parseFloat(parts[1]);
        if (!isNaN(termLength) && termLength > 0) newFormData.term_length = termLength;
        if (!isNaN(factor) && factor > 0) newFormData.factor_rate = factor.toString();
      }
    }
    // For funding_amount / factor_rate / term_length changes, just recompute derived values
    
    setFormData(recomputeDerived(newFormData));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.funding_amount || !formData.payback_amount || !formData.payment_amount) {
      toast({ title: 'Error', description: 'Please fill in all required fields', variant: 'destructive' });
      return;
    }

    setLoading(true);
    try {
      const token = localStorage.getItem('mcaToken');
      await axios.post(`${API_BASE}/deals/${deal.id}/fund`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast({ title: 'Success', description: 'Deal moved to Funded successfully' });
      onSuccess();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to fund deal', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-8 max-w-3xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold" data-testid="funding-modal-title">Enter Funding Terms</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="funding-modal-close">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Funding Amount *</label>
              <Input
                required
                type="number"
                data-testid="funding-amount-input"
                value={formData.funding_amount}
                onChange={(e) => handleTermOrAmountChange('funding_amount', e.target.value)}
                placeholder="50000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Term/Factor * (e.g., 110/1.45)</label>
              <Input
                required
                data-testid="term-factor-input"
                value={formData.term_input}
                onChange={(e) => handleTermOrAmountChange('term_input', e.target.value)}
                placeholder="110/1.45"
              />
              <p className="text-xs text-gray-500 mt-1">Format: Days/Factor (auto-calculates payback)</p>
            </div>
          </div>

          {/* Separate Factor Rate + Term Days inputs (additive — combined Term/Factor above still works) */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Factor Rate *</label>
              <Input
                required
                type="number"
                step="0.01"
                data-testid="factor-rate-input"
                value={formData.factor_rate}
                onChange={(e) => handleTermOrAmountChange('factor_rate', e.target.value)}
                placeholder="1.45"
              />
              <p className="text-xs text-gray-500 mt-1">Funding × Factor = Payback</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Term Days *</label>
              <Input
                required
                type="number"
                data-testid="term-days-input"
                value={formData.term_length}
                onChange={(e) => handleTermOrAmountChange('term_length', e.target.value)}
                placeholder="110"
              />
              <p className="text-xs text-gray-500 mt-1">Payback ÷ Term Days = Daily Payment</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Payback Amount (Auto-calculated)</label>
              <Input
                type="number"
                data-testid="payback-amount-input"
                value={formData.payback_amount}
                onChange={(e) => setFormData({...formData, payback_amount: e.target.value})}
                placeholder="72500"
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Funding x Factor = Payback</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Payment Amount (Auto-calculated)</label>
              <Input
                type="number"
                data-testid="payment-amount-input"
                value={formData.payment_amount}
                onChange={(e) => setFormData({...formData, payment_amount: e.target.value})}
                placeholder="659.09"
                className="bg-gray-50"
              />
              <p className="text-xs text-gray-500 mt-1">Payback / Term = Payment</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Payment Frequency *</label>
              <select
                required
                data-testid="payment-frequency-select"
                value={formData.payment_frequency}
                onChange={(e) => setFormData({...formData, payment_frequency: e.target.value})}
                className="w-full border rounded p-2"
              >
                <option value="daily">Daily</option>
                <option value="weekly">Weekly</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">First Payment Date *</label>
              <Input
                required
                type="date"
                data-testid="first-payment-date-input"
                value={formData.first_payment_date}
                onChange={(e) => setFormData({...formData, first_payment_date: e.target.value})}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Funding Date *</label>
            <Input
              required
              type="date"
              data-testid="funding-date-input"
              value={formData.funding_date}
              onChange={(e) => setFormData({...formData, funding_date: e.target.value})}
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Notes</label>
            <textarea
              data-testid="funding-notes-textarea"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              className="w-full border rounded p-2 h-24"
              placeholder="Optional notes about this funding..."
            />
          </div>

          <div className="bg-blue-50 p-4 rounded" data-testid="funding-summary">
            <p className="text-sm text-gray-700 mb-2"><strong>Summary:</strong></p>
            <p className="text-sm text-gray-600" data-testid="summary-funding">Funding: ${Number(formData.funding_amount || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600" data-testid="summary-payback">Payback: ${Number(formData.payback_amount || 0).toLocaleString()}</p>
            <p className="text-sm text-gray-600" data-testid="summary-payments">{formData.term_length} {formData.payment_frequency} payments of ${formData.payment_amount}</p>
          </div>

          <div className="flex gap-3 pt-4 border-t">
            <Button type="submit" disabled={loading} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="fund-deal-submit-btn">
              {loading ? 'Processing...' : 'Fund Deal & Generate Schedule'}
            </Button>
            <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={loading} data-testid="fund-deal-cancel-btn">
              Cancel
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FundingTermsModal;
